import { OpraRag } from '@interfaces/opra-chart.interface';
import { StackedChartData } from '@interfaces-clean/panels.interface';
import { stackedChartTooltipFormatter } from '@pages-clean/risk-assessment/risk-dashboard/sections/chart-utils';
import { getUTCDate } from '@utils-clean/date-time-helpers';
import Constants from '@utils/constants';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

export const unitOptions = [2, 3, 4, 5];
export const defaultUnit = 3;
export enum RiskTrendPeriod {
    Weeks,
    Months,
    Quarters,
    HalfYears,
    Years,
}

export const periodArray = ['Weeks', 'Months', 'Quarters', 'Half Years', 'Years'];

const startOfWeek = (date: Date) => {
    const day = date.getUTCDay();
    const diff = (day === 0 /* sunday */ ? 7 : day) - 1;
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - diff));
};

const endOfWeek = (date: Date) => {
    const day = date.getUTCDay();
    const diff = day === 0 /* sunday */ ? 0 : 7 - day;
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + diff + 1, 0, 0, 0, -1));
};

const startOfMonth = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
const endOfMonth = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
const startOfQuarter = (date: Date) =>
    new Date(Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1));
const endOfQuarter = (date: Date) =>
    new Date(Date.UTC(date.getUTCFullYear(), (Math.floor(date.getUTCMonth() / 3) + 1) * 3, 0));
const startOfHalfYear = (date: Date) =>
    new Date(Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 6) * 6, 1));
const endOfHalfYear = (date: Date) =>
    new Date(Date.UTC(date.getUTCFullYear(), (Math.floor(date.getUTCMonth() / 6) + 1) * 6, 0));
const startOfYear = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
const endOfYear = (date: Date) => new Date(Date.UTC(date.getUTCFullYear() + 1, 0, 0));

const periodStart = (date: Date, periodType: RiskTrendPeriod): Date => {
    switch (periodType) {
        case RiskTrendPeriod.Weeks:
            return startOfWeek(date);

        case RiskTrendPeriod.Months:
            return startOfMonth(date);

        case RiskTrendPeriod.Quarters:
            return startOfQuarter(date);

        case RiskTrendPeriod.HalfYears:
            return startOfHalfYear(date);

        default:
            return startOfYear(date);
    }
};

const periodEnd = (date: Date, periodType: RiskTrendPeriod): Date => {
    switch (periodType) {
        case RiskTrendPeriod.Weeks:
            return endOfWeek(date);

        case RiskTrendPeriod.Months:
            return endOfMonth(date);

        case RiskTrendPeriod.Quarters:
            return endOfQuarter(date);

        case RiskTrendPeriod.HalfYears:
            return endOfHalfYear(date);

        default:
            return endOfYear(date);
    }
};

const priorPeriodEnd = (date: Date, periodType: RiskTrendPeriod): Date => {
    const thisPeriodStart = periodStart(date, periodType);

    return new Date(
        Date.UTC(
            thisPeriodStart.getUTCFullYear(),
            thisPeriodStart.getUTCMonth(),
            thisPeriodStart.getUTCDate(),
            0,
            0,
            0,
            -1,
        ),
    );
};

const categoryLabelFromPeriodEnd = (periodEndDate: Date, periodType: RiskTrendPeriod): string => {
    const d = getUTCDate();
    const todayUTC = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    if (periodEndDate >= todayUTC) return '(Current)';

    if (periodType === RiskTrendPeriod.Weeks) {
        const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            timeZone: 'UTC',
        });

        const [{ value: day }, , { value: month }, , { value: year }] = dateTimeFormat.formatToParts(periodEndDate);

        return `${day}-${month}-${year}`;
    }

    if (periodType === RiskTrendPeriod.Months) {
        const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
            timeZone: 'UTC',
        });

        const [, , { value: month }, , { value: year }] = dateTimeFormat.formatToParts(periodEndDate);

        return `${month} ${year}`;
    }

    if (periodType === RiskTrendPeriod.Quarters)
        return `Q${Math.floor(periodEndDate.getUTCMonth() / 3) + 1} ${periodEndDate.getUTCFullYear()}`;

    if (periodType === RiskTrendPeriod.HalfYears)
        return `${
            Math.floor(periodEndDate.getUTCMonth() / 6) === 0 ? 'First' : 'Second'
        } Half ${periodEndDate.getUTCFullYear()}`;

    return periodEndDate.getUTCFullYear().toString();
};

export const defaultPeriod = RiskTrendPeriod.Months;

const ragSeriesOrder = [OpraRag.Red, OpraRag.Amber, OpraRag.Green] as const;

type RagSeriesColor = (typeof ragSeriesOrder)[number];

const ragNames: Record<RagSeriesColor, string> = {
    [OpraRag.Red]: 'Red',
    [OpraRag.Amber]: 'Amber',
    [OpraRag.Green]: 'Green',
};

interface CategoryOption {
    id: string | null;
    name: string;
}

export const getXAxisTitle = (periodType: RiskTrendPeriod): string => {
    const xAxisTitleKeys: Record<RiskTrendPeriod, string> = {
        [RiskTrendPeriod.Weeks]: 'riskDashboard.riskTrends.xAxisTitles.weekEnding',
        [RiskTrendPeriod.Months]: 'riskDashboard.riskTrends.xAxisTitles.months',
        [RiskTrendPeriod.Quarters]: 'riskDashboard.riskTrends.xAxisTitles.quarters',
        [RiskTrendPeriod.HalfYears]: 'riskDashboard.riskTrends.xAxisTitles.halfYears',
        [RiskTrendPeriod.Years]: 'riskDashboard.riskTrends.xAxisTitles.years',
    };
    return xAxisTitleKeys[periodType];
};

export const extractCategoryOptions = (risks: CompleteRiskFields[]): CategoryOption[] => {
    const seen = new Map<string, string>();

    risks.forEach((r) => {
        if (!r.category?.id) return;
        if (!seen.has(r.category.id)) seen.set(r.category.id, r.category.name ?? r.category.id);
    });

    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
};

const generatePeriodEnds = (periodType: RiskTrendPeriod, unit: number): Date[] => {
    const d = getUTCDate();
    const todayUTC = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    let ends: Date[] = [periodEnd(todayUTC, periodType)];

    for (let i = 0; i < unit; i++) {
        ends = [priorPeriodEnd(ends[0], periodType), ...ends];
    }

    return ends;
};

const getLatestEvalAtOrBefore = (risk: CompleteRiskFields, cutoff: Date) => {
    const history = (risk.evaluationHistory ?? []).filter((e) => e?.created && new Date(e.created) <= cutoff);

    if (!history.length) return null;

    return history.reduce((latest, current) =>
        new Date(current?.created ?? 0) > new Date(latest?.created ?? 0) ? current : latest,
    );
};

const getLatestEval = (risk: CompleteRiskFields) => {
    const history = [...(risk.evaluationHistory ?? [])];
    if (!history.length) return null;

    return history.reduce((latest, current) =>
        new Date(current?.created ?? 0) > new Date(latest?.created ?? 0) ? current : latest,
    );
};

const computeRagCounts = (
    risks: CompleteRiskFields[],
    evalGetter: (r: CompleteRiskFields) => { colour?: string | null } | null,
): Record<RagSeriesColor, number> => {
    const counts: Record<RagSeriesColor, number> = {
        [OpraRag.Red]: 0,
        [OpraRag.Amber]: 0,
        [OpraRag.Green]: 0,
    };

    risks.forEach((r) => {
        const colour = evalGetter(r)?.colour?.toUpperCase() ?? 'NONE';
        if (colour === 'RED') counts[OpraRag.Red]++;
        else if (colour === 'AMBER') counts[OpraRag.Amber]++;
        else if (colour === 'GREEN') counts[OpraRag.Green]++;
    });

    return counts;
};

export const buildTrendsChartData = (
    risks: CompleteRiskFields[],
    periodType: RiskTrendPeriod,
    unit: number,
    categoryId: string | null,
): StackedChartData => {
    const risksWithEvaluations = risks.filter((r) => (r.evaluationHistory?.length ?? 0) > 0);
    const activeRisksWithEvaluations = risksWithEvaluations.filter((r) => String(r.status).toUpperCase() !== 'CLOSED');

    const filterByCategory = (rs: CompleteRiskFields[]) =>
        categoryId ? rs.filter((r) => (r.category?.id ?? Constants.ObjectId.Empty) === categoryId) : rs;

    const filteredRisks = filterByCategory(risksWithEvaluations);
    const filteredActiveRisks = filterByCategory(activeRisksWithEvaluations);

    const allPeriodEnds = generatePeriodEnds(periodType, unit);
    const isMonths = periodType === RiskTrendPeriod.Months;
    const historicalEnds = isMonths ? allPeriodEnds : allPeriodEnds.slice(0, -1);

    const historicalCounts = historicalEnds.map((end) => {
        const d = getUTCDate();
        const todayUTC = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        return computeRagCounts(end >= todayUTC ? filteredActiveRisks : filteredRisks, (r) =>
            getLatestEvalAtOrBefore(r, end),
        );
    });

    const todayCounts = isMonths ? null : computeRagCounts(filteredActiveRisks, getLatestEval);

    const allCounts = todayCounts ? [...historicalCounts, todayCounts] : historicalCounts;

    const xCategories = [
        ...historicalEnds.map((end) => categoryLabelFromPeriodEnd(end, periodType)),
        ...(todayCounts ? ['(Current)'] : []),
    ];

    const hasCounts = allCounts.some(
        (count) => count[OpraRag.Red] > 0 || count[OpraRag.Amber] > 0 || count[OpraRag.Green] > 0,
    );

    const series = hasCounts
        ? ragSeriesOrder.map((rag) => ({
              name: ragNames[rag],
              values: allCounts.map((counts) => counts[rag]),
          }))
        : [];

    return { series, xCategories, tooltipFormatter: stackedChartTooltipFormatter };
};
